import { config } from '../../config/config';
import authService from '../../services/authService';
import { AccountSwitchService } from '../../api/services/AccountSwitchService';
import type { Routemize_DTOs_Accounts_MyAccountDto } from '../../api/models/Routemize_DTOs_Accounts_MyAccountDto';

export type MyAccountDto = Routemize_DTOs_Accounts_MyAccountDto;

const ACCOUNT_SESSION_KEY = 'account_session';

/** Refresh this far ahead of expiry so an in-flight request never rides a token that dies mid-call. */
const REFRESH_LEAD_MS = 90_000;

interface StoredAccountSession {
  accessToken: string;
  refreshToken: string | null;
  /** Epoch ms. */
  expiresAt: number;
  tenantId: string;
  tenantName: string;
  /** Origin the switch started from, so leaving can return there instead of dumping you at login. */
  fromOrigin: string | null;
}

/**
 * Holds the session created by switching into another account the signed-in person owns.
 *
 * One session per origin, not a map. A switch hops to the target account's subdomain, and
 * `localStorage` is per-origin — so each origin naturally holds at most one switched session, and a
 * map would only ever have a single entry. Switching back to an account the person is natively
 * logged into lands on that origin's own OIDC session instead.
 *
 * Distinct from `ImpersonationManager`: that stores a token for *someone else's* user row and shows
 * an alarm banner. This is the person's own user row in their own account, so it is a normal
 * session — it just was not created by an interactive login.
 */
class AccountSessionManager {
  private refreshInFlight: Promise<string | null> | null = null;

  async getMyAccounts(): Promise<MyAccountDto[]> {
    return AccountSwitchService.getApiAppAccountSwitchGetMyAccounts();
  }

  /**
   * Starts a switch: asks the API for a one-time code, then navigates to the target subdomain to
   * redeem it. Nothing is minted here — the token is created on the destination origin, so it never
   * travels through the redirect.
   */
  async switchTo(tenantId: string, landingPath: string = '/dashboard'): Promise<void> {
    const result = await AccountSwitchService.postApiAppAccountSwitchCreateSwitchCode({ tenantId });

    if (!result.code || !result.subdomain) {
      throw new Error('The server did not return a switch code.');
    }

    window.location.href = this.buildSwitchUrl(result.subdomain, result.code, landingPath);
  }

  /**
   * Builds the destination URL, swapping the target subdomain into the current host so it stays
   * correct in local development (`acme.localhost:4200`) as well as production.
   * Mirrors `buildCompanyUrl` in utils/companyUrl.
   */
  buildSwitchUrl(subdomain: string, code: string, landingPath: string): string {
    const { protocol, hostname, port } = window.location;
    const parts = hostname.split('.');
    const hasSubdomain = parts.length > 2 || (parts.length === 2 && parts[1] === 'localhost');
    const baseDomain = hasSubdomain ? parts.slice(1).join('.') : hostname;
    const portSuffix = port ? `:${port}` : '';

    const target = new URL(`${protocol}//${subdomain}.${baseDomain}${portSuffix}/switch`);
    target.searchParams.set('code', code);
    target.searchParams.set('to', landingPath);
    // Where we came from, so leaving the switched session can go back rather than to login.
    target.searchParams.set('from', window.location.origin);
    return target.toString();
  }

  /**
   * Redeems a one-time code at the token endpoint and stores the resulting session.
   * Runs on the destination origin, where the person has no session yet — the code is the only
   * credential, which is why it is single-use and lives ~60s.
   */
  async redeemCode(code: string, fromOrigin?: string | null): Promise<void> {
    const body = new URLSearchParams({
      grant_type: 'AccountSwitch',
      client_id: config.auth.clientId,
      switch_code: code,
      scope: 'openid profile email offline_access Routemize',
    });

    const response = await fetch(`${config.auth.authority}connect/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(error?.error_description || error?.error || 'Could not switch accounts.');
    }

    const tokens = await response.json();
    if (!tokens.access_token) {
      throw new Error('No access token in response.');
    }

    this.store({
      accessToken: tokens.access_token,
      refreshToken: tokens.refresh_token ?? null,
      expiresAt: Date.now() + (tokens.expires_in ?? 3600) * 1000,
      tenantId: authService.getTenantIdFromToken(tokens.access_token) ?? '',
      tenantName: '',
      fromOrigin: this.sameSiteOrigin(fromOrigin),
    });

    // The cached ABP config belongs to whoever was here before — force a re-fetch under the new token.
    localStorage.removeItem('app_config');
    localStorage.removeItem('app_config_version');
  }

  getSession(): StoredAccountSession | null {
    const raw = localStorage.getItem(ACCOUNT_SESSION_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as StoredAccountSession;
    } catch {
      // Unreadable entry is worse than none — drop it rather than wedging every token read.
      localStorage.removeItem(ACCOUNT_SESSION_KEY);
      return null;
    }
  }

  isActive(): boolean {
    return this.getSession() !== null;
  }

  /**
   * The `sub` claim of the switched session — the person's user id in the account they switched
   * into. Used where a caller needs to identify the effective user but there is no OIDC user on
   * this origin (e.g. the 2FA status cache key).
   */
  getSubject(): string | null {
    return this.readClaim('sub');
  }

  /**
   * The operator's name when this switched session was started from a staff impersonation, else
   * null. The impersonation banner cannot use `ImpersonationManager` after a switch — that reads a
   * `localStorage` key that lives on the *source* origin — so the claim on the token is the only
   * thing that survives the hop.
   */
  getImpersonatorName(): string | null {
    return this.readClaim('impersonator_username') ?? this.readClaim('impersonator_userid');
  }

  isImpersonated(): boolean {
    return !!this.readClaim('impersonator_userid');
  }

  private readClaim(name: string): string | null {
    const session = this.getSession();
    if (!session) return null;
    try {
      const parts = session.accessToken.split('.');
      if (parts.length !== 3) return null;
      const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
      const value = payload[name];
      return value ? String(value) : null;
    } catch {
      return null;
    }
  }

  /**
   * The account-session token for API calls, refreshed on the way out when it is close to expiry.
   * Returns null once the session cannot be kept alive, so the caller falls through to the origin's
   * own OIDC session (or to login).
   */
  async getAccessToken(): Promise<string | null> {
    const session = this.getSession();
    if (!session) return null;

    if (Date.now() < session.expiresAt - REFRESH_LEAD_MS) {
      return session.accessToken;
    }

    if (!session.refreshToken) {
      // Nothing to renew with — the session is spent.
      this.clear();
      return null;
    }

    // De-dupe: several concurrent calls must not each redeem the same rotating refresh token.
    if (!this.refreshInFlight) {
      this.refreshInFlight = this.refresh(session).finally(() => {
        this.refreshInFlight = null;
      });
    }

    return this.refreshInFlight;
  }

  private async refresh(session: StoredAccountSession): Promise<string | null> {
    try {
      const body = new URLSearchParams({
        grant_type: 'refresh_token',
        client_id: config.auth.clientId,
        refresh_token: session.refreshToken as string,
      });

      const response = await fetch(`${config.auth.authority}connect/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (!response.ok) {
        this.clear();
        return null;
      }

      const tokens = await response.json();
      if (!tokens.access_token) {
        this.clear();
        return null;
      }

      this.store({
        ...session,
        accessToken: tokens.access_token,
        // OpenIddict rotates refresh tokens; keep the new one or the next renewal fails.
        refreshToken: tokens.refresh_token ?? session.refreshToken,
        expiresAt: Date.now() + (tokens.expires_in ?? 3600) * 1000,
      });

      return tokens.access_token;
    } catch {
      this.clear();
      return null;
    }
  }

  /**
   * Drops a switched session that has been made redundant by this origin's own login — i.e. the
   * person logged in natively to the same account they had switched into. Without this the stale
   * switched token would keep outranking the real session.
   */
  reconcileWithNativeSession(nativeTenantId: string | null): void {
    const session = this.getSession();
    if (session && session.tenantId === (nativeTenantId ?? '')) {
      this.clear();
    }
  }

  setTenantName(tenantName: string): void {
    const session = this.getSession();
    if (session) {
      this.store({ ...session, tenantName });
    }
  }

  clear(): void {
    localStorage.removeItem(ACCOUNT_SESSION_KEY);
  }

  /**
   * Leaves the switched account. Returns to the origin the switch started from when we know it —
   * that origin still holds its own session (including an impersonation in progress), so going
   * back there resumes it instead of stranding the operator at a login screen.
   */
  exit(): void {
    const target = this.getSession()?.fromOrigin;
    this.clear();
    localStorage.removeItem('app_config');
    localStorage.removeItem('app_config_version');
    window.location.href = target ? `${target}/` : '/';
  }

  /**
   * Accepts an origin only when it sits on the same base domain as this one, so a tampered `from`
   * parameter cannot turn the exit button into an open redirect.
   */
  private sameSiteOrigin(origin?: string | null): string | null {
    if (!origin) return null;
    try {
      const candidate = new URL(origin);
      const base = (host: string) => host.split('.').slice(-2).join('.');
      return base(candidate.hostname) === base(window.location.hostname) ? candidate.origin : null;
    } catch {
      return null;
    }
  }

  private store(session: StoredAccountSession): void {
    localStorage.setItem(ACCOUNT_SESSION_KEY, JSON.stringify(session));
  }
}

export const accountSessionManager = new AccountSessionManager();
